/** Shared time maths for the clock widgets, the detail pages and the game. */

export const DOZENAL_DIGITS = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "χ", "ε"];

export const UNITS_PER_DAY = 248832;
export const SECONDS_PER_DAY = 86400;

/** Fraction of the local day elapsed, in [0, 1). */
export function dayProgress(now = new Date()): number {
  return (
    (now.getHours() * 3600 +
      now.getMinutes() * 60 +
      now.getSeconds() +
      now.getMilliseconds() / 1000) /
    SECONDS_PER_DAY
  );
}

/** Five dozenal digits of the day fraction, without the leading point. */
export function toDozenal(progress: number): string {
  const units = Math.floor(progress * UNITS_PER_DAY);
  let out = "";
  for (const divisor of [20736, 1728, 144, 12, 1]) {
    out += DOZENAL_DIGITS[Math.floor(units / divisor) % 12];
  }
  return out;
}

export function dozenalFromUnits(units: number): string {
  const clamped = Math.max(0, Math.min(UNITS_PER_DAY - 1, Math.floor(units)));
  let out = ".";
  for (const divisor of [20736, 1728, 144, 12, 1]) {
    out += DOZENAL_DIGITS[Math.floor(clamped / divisor) % 12];
  }
  return out;
}

/** Parses a dozenal fraction like ".3χ07ε"; x/e are accepted for χ/ε. */
export function parseDozenal(value: string): number | null {
  const body = value.startsWith(".") ? value.slice(1) : value;
  if (!body.length) return null;

  let units = 0;
  for (const character of body) {
    const lower = character.toLowerCase();
    const mapped = lower === "x" ? "χ" : lower === "e" ? "ε" : character;
    const index = DOZENAL_DIGITS.indexOf(mapped);
    if (index === -1) return null;
    units = units * 12 + index;
  }

  for (let i = body.length; i < 5; i++) units *= 12;
  return units;
}

/** Parses "H:MM" or "H:MM:SS" into seconds, or null when malformed. */
export function parseHms(value: string): number | null {
  const parts = value.split(":").map((part) => Number(part.trim()));
  if (parts.length < 2 || parts.length > 3) return null;
  if (parts.some((part) => !Number.isFinite(part))) return null;

  const [hours = 0, minutes = 0, seconds = 0] = parts;
  return hours * 3600 + minutes * 60 + seconds;
}

export function formatHms(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.round(totalSeconds % 60);
  return `${hours}h ${minutes}m ${seconds}s`;
}

export function formatHmsColon(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.round(totalSeconds % 60);
  return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}
